/**
 * パイバル（係留気球）の角度読み取りから風を推定する。
 *
 * クルーが紐で係留したヘリウム風船（パイバル）は、風船の正味浮力（揚力）と
 * 風による抗力のつり合いで、紐が鉛直から傾く。この傾き角から風速を、
 * 紐の向き（コンパス方位）から風向を逆算できる。
 *
 *   抗力 = 揚力 × tan(鉛直からの傾き角)
 *   抗力 = 0.5 × 空気密度 × 風速² × 抗力係数 × 断面積
 *   → 風速 = √( k × tan(傾き角) )   ただし k = 2×揚力 / (空気密度×抗力係数×断面積)
 *
 * kは風船の個体差（浮力・大きさ）に依存するため、物理パラメータから計算する
 * より、既知の基準風速（携帯風速計等）と実測の傾き角から逆算する実測校正を
 * 基本とする（`calibrateKFromReference`）。
 *
 * ★ 紐がまっすぐ張っている（弛まない）前提の単純化モデル。紐自体の重さ・
 *   空気抵抗は無視する。既存の理論投下（パイロットバルーン・セオドライト法）
 *   と同じ単純化。
 */

/** 風速 [m/s] = √(k × tan(傾き角)) の校正定数 k [m²/s²] */
export interface PibalCalibration {
  k: number;
}

/**
 * 紐の鉛直からの傾き角から風速を求める。
 * @param tiltFromVerticalDeg 紐の鉛直からの傾き角 [度]。0=無風（真上）、90に近いほど強風
 */
export function windSpeedFromTilt(tiltFromVerticalDeg: number, calib: PibalCalibration): number {
  const t = Math.max(0, Math.min(89.5, tiltFromVerticalDeg));
  const tanT = Math.tan((t * Math.PI) / 180);
  return Math.sqrt(Math.max(0, calib.k * tanT));
}

/**
 * 既知の基準風速（携帯風速計等での同時観測値）と、そのときの傾き角から
 * 校正定数kを逆算する。
 */
export function calibrateKFromReference(referenceSpeedMs: number, tiltFromVerticalDeg: number): number {
  const t = Math.max(0.5, Math.min(89.5, tiltFromVerticalDeg));
  const tanT = Math.tan((t * Math.PI) / 180);
  return (referenceSpeedMs * referenceSpeedMs) / tanT;
}

/**
 * 風船の正味浮力・抗力係数×断面積が分かっている場合に、物理パラメータから
 * kを計算する（規格化されたパイバルを使う場合の参考用。既定は実測校正）。
 * @param netLiftN 風船の正味浮力（揚力−自重、紐の重さ含む）[N]
 * @param dragCoefficientArea 抗力係数×断面積 (Cd×A) [m²]
 * @param airDensity 空気密度 [kg/m³]。既定は海面標準大気(1.225)
 */
export function calibrateKFromPhysics(
  netLiftN: number,
  dragCoefficientArea: number,
  airDensity = 1.225,
): number {
  return (2 * netLiftN) / (airDensity * dragCoefficientArea);
}

export interface PibalGeometry {
  /** アンカー（クルーの立ち位置）からの高さ [m] */
  heightAboveAnchorM: number;
  /** アンカーからの水平距離 [m] */
  horizontalOffsetM: number;
}

/**
 * 紐がまっすぐ張っている前提で、紐の長さと傾き角から風船の位置（アンカー
 * からの相対座標）を求める。
 */
export function pibalGeometry(stringLengthM: number, tiltFromVerticalDeg: number): PibalGeometry {
  const rad = (Math.max(0, Math.min(90, tiltFromVerticalDeg)) * Math.PI) / 180;
  return {
    heightAboveAnchorM: stringLengthM * Math.cos(rad),
    horizontalOffsetM: stringLengthM * Math.sin(rad),
  };
}

/**
 * 観測の確からしさ（0〜1）。
 *
 * GPS由来のWindSampleより精度で劣る手動観測法であるため、上限を0.6に抑える。
 * 傾きが小さい（=無風に近い）ほど、角度の読み取り誤差に対して風速の相対誤差が
 * 大きくなる（tanの微分が小さい角度域で緩やか→風速誤差が支配的になりやすい）
 * ため、3°未満はさらに下げる。
 */
export function pibalQuality(tiltFromVerticalDeg: number): number {
  if (tiltFromVerticalDeg < 3) return 0.3;
  return 0.6;
}
